import { useEffect, useMemo, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  Columns3,
  Search,
  Package,
  ShoppingCart,
  Truck,
  Users,
  Settings,
  ClipboardCheck,
  Receipt,
  BarChart3,
  MessageSquare,
  Shield,
  Check,
  RefreshCw,
  WifiOff,
  MoreHorizontal,
  HelpCircle,
  ArrowUpRight,
  HardHat,
  IdCard,
} from 'lucide-react';
import { HELP_CENTER_URL } from '@/config/helpLinks';
import {
  getUserCapabilities,
  getUserRole,
  ROLE_LABELS,
  normalizeReviewShopKey,
  useBoards,
  useChatStatus,
  useComplianceScope,
  useReviewQueueCount,
  type Board,
} from '@field-tickets/shared';
import { useAuthStore } from '@/store/auth';
import { useImpersonationStore } from '@/store/impersonation';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { MemoryTicketSaveNotice } from '@/components/MemoryTicketSaveNotice';
import { UserMenu } from '@/components/ui/UserMenu';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { ToastRouteClearer } from '@/components/Toast';
import { useSyncStatus } from '@/hooks/useSyncStatus';
import { useIsMobile } from '@/hooks';
import { recordVisitedPath } from '@/utils/lastRoute';
import { warmRouteChunk } from '@/routeChunks';

/**
 * Valve brand mark — mirrors the design-spec `Icon name="valve"` paths
 * (center circle + 4 radial arms). Kept inline so the topbar doesn't pay
 * for an extra lucide import.
 */
function ValveMark({ size = 15, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: 'none' }}
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 4v4" />
      <path d="M12 16v4" />
      <path d="M4 12h4" />
      <path d="M16 12h4" />
    </svg>
  );
}

/**
 * Sidebar "My boards" is pinned to the two active regions. Keyed by
 * case-insensitive substring match against `board.name` so minor rename drift
 * in Trello doesn't drop the entry.
 */
const PINNED_BOARDS: Array<{ match: string; label: string; color: string }> = [
  { match: 'oklahoma', label: 'Oklahoma Comm Projects', color: '#0b6fc2' },
  { match: 'permian basin', label: 'Permian Basin', color: '#d8480f' },
];

/**
 * A sidebar entry. `external: true` renders an <a target="_blank"> with the
 * external-link glyph instead of a routed <NavLink> (Help & Guides today).
 */
type NavItem = {
  to: string;
  label: string;
  icon: ReactNode;
  external?: boolean;
};

const baseNav: NavItem[] = [
  { to: '/dashboard', label: 'Home', icon: <LayoutGrid size={16} /> },
  { to: '/sites', label: 'Board', icon: <Columns3 size={16} /> },
];
const reviewNav: NavItem = { to: '/review', label: 'Review', icon: <ClipboardCheck size={16} /> };
const salesNav: NavItem = { to: '/sales', label: 'Sales', icon: <Receipt size={16} /> };
const purchasingNav: NavItem = { to: '/purchase-orders', label: 'Purchasing', icon: <ShoppingCart size={16} /> };
const customersNav: NavItem = { to: '/customers', label: 'Customers', icon: <Users size={16} /> };
const vendorsNav: NavItem = { to: '/vendors', label: 'Vendors', icon: <Truck size={16} /> };
const reportsNav: NavItem = { to: '/reports', label: 'Reports', icon: <BarChart3 size={16} /> };
const chatNav: NavItem = { to: '/chat', label: 'Chat', icon: <MessageSquare size={16} /> };
const safetyNav: NavItem = { to: '/safety', label: 'Safety', icon: <HardHat size={16} /> };
const myDocumentsNav: NavItem = { to: '/my-documents', label: 'My documents', icon: <IdCard size={16} /> };
// The catalog route is unchanged; the rail calls it "Items" per the redesign's
// IA contract (the page is the QuickBooks item catalog).
const catalogNav: NavItem = { to: '/catalog', label: 'Items', icon: <Package size={16} /> };
const adminNav: NavItem = { to: '/admin', label: 'Admin', icon: <Shield size={16} /> };
const settingsNav: NavItem = { to: '/settings', label: 'Settings', icon: <Settings size={16} /> };
const helpNav: NavItem = {
  to: HELP_CENTER_URL,
  label: 'Help & Guides',
  icon: <HelpCircle size={16} />,
  external: true,
};

// Mobile bottom-nav matches the design-spec MobileTabBar: four entries —
// Home / Board / Search / More. "More" deep-links to `/settings` which in turn
// exposes Team / Catalog / Admin for users who can see them. Keeping the tab
// bar at four items preserves the Apple-leaning look from the prototype.
const mobileItems = [
  { to: '/dashboard', label: 'Home', icon: <LayoutGrid size={20} /> },
  { to: '/sites', label: 'Board', icon: <Columns3 size={20} /> },
  { to: '/search', label: 'Search', icon: <Search size={20} /> },
  { to: '/settings', label: 'More', icon: <MoreHorizontal size={20} /> },
];

function ReviewQueueCountBadge() {
  const user = useAuthStore((s) => s.user);
  const defaultReviewShopKey = normalizeReviewShopKey(user?.defaultReviewShopKey);
  // Badge polls the lightweight count endpoint instead of the full queue. The
  // endpoint already counts only actionable tickets (invoiced lingering rows are
  // excluded server-side), matching what the review page's default view shows.
  const reviewQueueCount = useReviewQueueCount(
    defaultReviewShopKey ? { shopKey: defaultReviewShopKey } : undefined,
  );
  const reviewCount = reviewQueueCount.data ?? 0;

  if (reviewCount <= 0) return null;

  return (
    <span className="ft-sidenav__count" aria-label={`${reviewCount} needing review`}>
      {reviewCount}
    </span>
  );
}

function SyncStatusIcon({ status }: { status: 'synced' | 'syncing' | 'offline' }) {
  const iconStyle = (visible: boolean) => ({ display: visible ? 'block' : 'none' });
  return (
    <span aria-hidden="true" style={{ width: 12, height: 12, flex: 'none' }}>
      <Check size={12} style={iconStyle(status === 'synced')} />
      <RefreshCw
        size={12}
        className="ft-topbar__pill-spin"
        style={iconStyle(status === 'syncing')}
      />
      <WifiOff size={12} style={iconStyle(status === 'offline')} />
    </span>
  );
}

function SyncStatusIndicator() {
  const status = useSyncStatus();
  const label = { synced: 'Synced', syncing: 'Syncing', offline: 'Offline' }[status];

  return (
    <span
      className={`ft-topbar__pill ft-topbar__pill--sync ft-topbar__pill--sync-${status}`}
      role="status"
      aria-live="polite"
      aria-label={`Sync status: ${label}`}
      data-sync-state={status}
      style={{ width: 82, boxSizing: 'border-box', justifyContent: 'center' }}
    >
      <SyncStatusIcon status={status} />
      <span>{label}</span>
    </span>
  );
}

function MobileSyncStatusIndicator() {
  const status = useSyncStatus();
  const isMobile = useIsMobile();
  if (!isMobile) return null;

  const text = {
    synced: 'Synced',
    syncing: 'Syncing…',
    offline: 'Offline — changes will sync when back online',
  }[status];

  return (
    <div
      className={`ft-mobile-sync-banner ft-mobile-sync-banner--${status}`}
      role="status"
      aria-live="polite"
      data-sync-state={status}
      // Keep the same box in flow while synced. A background refetch can now
      // reveal it without shifting the routed page between pointerdown/click.
      style={{ visibility: status === 'synced' ? 'hidden' : 'visible' }}
    >
      <SyncStatusIcon status={status} />
      <span>{text}</span>
    </div>
  );
}

export function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const impersonationSession = useImpersonationStore((s) => s.session);
  const navigate = useNavigate();
  const location = useLocation();
  const capabilities = user ? getUserCapabilities(user) : null;
  const canViewTeam = capabilities?.canViewTeam ?? false;
  const canViewReview = capabilities?.canViewReviewQueue ?? false;
  const canViewSales = capabilities?.canViewSales ?? false;
  const canManagePurchaseOrders = capabilities?.canManagePurchaseOrders ?? false;
  const canReceiveInventory = capabilities?.canReceiveInventory ?? false;
  const canViewRates = capabilities?.canViewRates ?? false;
  const canManageKits = capabilities?.canManageKits ?? false;
  const canEditNicknames = capabilities?.canEditNicknames ?? false;
  // Admin opens for member managers (Admin/Manager/Sales, #2103); its
  // admin-only tabs check canManageUsers inside the page.
  const canManageMembers = capabilities?.canManageMembers ?? false;
  const chatStatus = useChatStatus();
  const canViewChat = chatStatus.data?.capabilities.canViewChat === true;
  // Compliance register (#1011): the scope endpoint 404s while the flag is
  // off (data null), which hides both entries.
  const complianceScope = useComplianceScope();
  const canViewSafety = complianceScope.data?.companies.some((c) => c.can_read) === true;
  const canViewMyDocuments = complianceScope.data?.self != null;

  const { data: boards } = useBoards();

  // Resolve the pinned Oklahoma + Permian Basin boards from the live board list
  // so the nav entries carry real board IDs (for filter/localStorage). If the
  // DB rename drifts, the pin list still shows labels with placeholder IDs.
  const pinnedBoards = useMemo(() => {
    const source = boards ?? [];
    return PINNED_BOARDS.map((pin) => {
      const match = source.find((b) => (b.name || '').toLowerCase().includes(pin.match));
      const board: Board = match ?? ({ id: `__pin_${pin.match}`, name: pin.label } as Board);
      // A pinned entry is "resolved" only when we matched a real board record.
      // Unresolved pins still render (so the user sees the menu) but they
      // navigate to /sites with no boardId — the kanban falls back to "all
      // boards" rather than 404.
      const resolved = Boolean(match);
      return { pin, board, resolved };
    });
  }, [boards]);

  // Slice F: highlight the sidebar entry that matches the active board scope.
  // We read the search param straight off the current location (not via
  // useSearchParams which would force a re-render outside the route subtree)
  // so the indicator updates whenever the user navigates within /sites.
  const activeBoardId = useMemo(() => {
    if (location.pathname !== '/sites') return '';
    try {
      return new URLSearchParams(location.search).get('boardId') || '';
    } catch {
      return '';
    }
  }, [location.pathname, location.search]);

  const roleLabel = useMemo(() => {
    if (!user) return '';
    const role = getUserRole(user);
    return ROLE_LABELS[role];
  }, [user]);

  const displayName = user?.fullName || 'User';

  // Build desktop sidebar entries — matches the redesign's three-group IA and
  // the existing per-item capability gating (unchanged; client visibility is
  // cosmetic, the real gates are the route guards and the server).
  // Search is intentionally omitted: it lives in the top-bar search button
  // (⌘K) rather than the rail. Team moved into the Admin page (admin-only).
  const workspaceItems: NavItem[] = [
    ...baseNav,
    ...(canViewReview ? [reviewNav] : []),
    ...(canViewSales ? [salesNav] : []),
    ...(canManagePurchaseOrders || canReceiveInventory ? [purchasingNav] : []),
    ...(canViewSafety ? [safetyNav] : []),
  ];

  const directoryItems: NavItem[] = [
    ...(canViewSales ? [customersNav] : []),
    ...(canManagePurchaseOrders ? [vendorsNav] : []),
  ];

  const toolsItems: NavItem[] = [
    ...(canManageKits || canEditNicknames ? [catalogNav] : []),
    ...(canViewRates ? [reportsNav] : []),
    ...(canViewChat ? [chatNav] : []),
    ...(canViewMyDocuments ? [myDocumentsNav] : []),
    ...(canManageMembers ? [adminNav] : []),
    settingsNav,
    helpNav,
  ];

  // Group headers "float": a group renders only when at least one of its items
  // survives capability gating, so a user without Directory access sees no
  // orphan "Directory" label and the remaining groups close up.
  const navGroups = [
    { label: 'Workspace', items: workspaceItems },
    { label: 'Directory', items: directoryItems },
    { label: 'Tools', items: toolsItems },
  ].filter((group) => group.items.length > 0);

  // Mobile bottom nav — fixed 4 items (Home / Board / Search / More) to match
  // the design-spec MobileTabBar. Team / Catalog / Admin live inside Settings
  // on mobile for role-gated users.

  const openSearch = () => {
    navigate('/search', { state: { focusSearchAt: Date.now() } });
  };

  // Feed the "where was the user before Settings" tracker used to default the
  // feedback form's page_path — see utils/lastRoute.ts.
  useEffect(() => {
    recordVisitedPath(location.pathname);
  }, [location.pathname]);

  // Wire the advertised ⌘K / Ctrl+K topbar hint to the same search action.
  // Ignore the shortcut while the user is typing in a field so it doesn't
  // hijack normal text entry.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Plain ⌘K / Ctrl+K only — don't hijack Shift/Alt combos (browser shortcuts).
      // Check modifiers before `key`: Chrome autofill dispatches keydown events
      // with no `key` (and no modifiers), which crashed `e.key.toLowerCase()`.
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || e.key?.toLowerCase() !== 'k') return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      openSearch();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // openSearch is stable enough for this effect; navigate is referentially
    // stable across renders, so re-binding on every render is unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shellClass = impersonationSession
    ? 'app-shell-outer app-shell-outer--impersonating'
    : 'app-shell-outer';

  return (
    <div className={shellClass}>
      <ToastRouteClearer />
      {impersonationSession && <ImpersonationBanner session={impersonationSession} />}
      <MemoryTicketSaveNotice />
      <div className="ft-app-shell">
        {/* Sidebar */}
        <aside className="ft-sidenav" aria-label="Primary navigation">
          <div className="ft-sidenav__brand">
            <span className="ft-sidenav__brand-mark" aria-hidden="true">
              <ValveMark size={15} color="white" />
            </span>
            <span className="ft-sidenav__brand-name">Alturex Work</span>
          </div>

          {navGroups.map((group) => (
            <div className="ft-sidenav__group" key={group.label}>
              <div className="ft-sidenav__section">{group.label}</div>
              {group.items.map((item) =>
                item.external ? (
                  /* External link (Outline help center) — opens in a new tab. */
                  <a
                    key={item.to}
                    className="ft-sidenav__item"
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="ft-sidenav__icon" aria-hidden="true">
                      {item.icon}
                    </span>
                    <span className="ft-sidenav__label">{item.label}</span>
                    <ArrowUpRight className="ft-sidenav__ext" aria-hidden="true" />
                  </a>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `ft-sidenav__item ${isActive ? 'ft-sidenav__item--active' : ''}`
                    }
                    end={item.to === '/dashboard'}
                    onMouseEnter={() => warmRouteChunk(item.to)}
                    onPointerDown={() => warmRouteChunk(item.to)}
                  >
                    <span className="ft-sidenav__icon" aria-hidden="true">
                      {item.icon}
                    </span>
                    <span className="ft-sidenav__label">{item.label}</span>
                    {item.to === '/review' && canViewReview ? <ReviewQueueCountBadge /> : null}
                  </NavLink>
                ),
              )}
            </div>
          ))}

          {/* "My boards" is the app's own fourth group — no mockup equivalent,
              rendered last so the three IA groups keep their fixed order. */}
          {pinnedBoards.length > 0 ? (
            <div className="ft-sidenav__group">
              <div className="ft-sidenav__section">My boards</div>
              {pinnedBoards.map(({ board, pin, resolved }) => {
                // Build /sites?boardId=<id> for resolved pins so the kanban
                // gets a real filter. Unresolved pins (no matching board row)
                // navigate to bare /sites so the user lands on the unscoped
                // view rather than a phantom filter.
                const to = resolved ? `/sites?boardId=${board.id}` : '/sites';
                const isActive = resolved && activeBoardId === board.id;
                return (
                  <NavLink
                    key={board.id}
                    to={to}
                    className={() =>
                      `ft-sidenav__item ft-sidenav__item--board${
                        isActive ? ' ft-sidenav__item--active' : ''
                      }`
                    }
                    aria-current={isActive ? 'page' : undefined}
                    onMouseEnter={() => warmRouteChunk('/sites')}
                    onPointerDown={() => warmRouteChunk('/sites')}
                    onClick={() => {
                      if (!resolved) return;
                      try {
                        localStorage.setItem('board_lastSelected', board.id);
                      } catch {
                        /* storage unavailable — ignore */
                      }
                    }}
                    title={pin.label}
                  >
                    <span
                      className="ft-sidenav__board-dot"
                      style={{ background: pin.color }}
                      aria-hidden="true"
                    />
                    <span className="ft-sidenav__label ft-sidenav__label--truncate">
                      {pin.label}
                    </span>
                  </NavLink>
                );
              })}
            </div>
          ) : null}
        </aside>

        {/* Top bar — brand now lives in the sidebar, so the bar reads
            [search] [spacer] [sync] [role] [help] [bell] [user]. */}
        <header className="ft-topbar" role="banner">
          <button
            type="button"
            className="ft-topbar__search-btn"
            aria-label="Search"
            onClick={openSearch}
          >
            <Search size={14} aria-hidden="true" className="ft-topbar__search-btn-icon" />
            <span className="ft-topbar__search-btn-label">Search</span>
            <kbd className="ft-topbar__search-btn-kbd" aria-hidden="true">⌘K</kbd>
          </button>

          <div className="ft-topbar__spacer" />

          <SyncStatusIndicator />

          {roleLabel ? (
            <span className="ft-topbar__pill ft-topbar__pill--role" aria-label={`Role: ${roleLabel}`}>
              {roleLabel}
            </span>
          ) : null}

          <a
            className="ft-topbar__icon-btn"
            href={HELP_CENTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Help & Guides"
            title="Help & Guides"
          >
            <HelpCircle size={18} aria-hidden="true" />
          </a>

          <NotificationBell />

          <UserMenu displayName={displayName} showAdmin={canManageMembers} />
        </header>

        {/* Main content */}
        <main className="ft-main" role="main">
          <MobileSyncStatusIndicator />
          <Outlet />
        </main>

        {/* Mobile bottom nav (hidden on desktop via CSS) */}
        <nav className="bottom-nav" aria-label="Mobile navigation">
          {mobileItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `bottom-nav__item ${isActive ? 'bottom-nav__item--active' : ''}`
              }
              end={item.to === '/dashboard'}
              onClick={item.to === '/search' ? openSearch : undefined}
              onPointerDown={() => warmRouteChunk(item.to)}
            >
              <span className="bottom-nav__icon">{item.icon}</span>
              <span className="bottom-nav__label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
